if (!customElements.get('product-carousel')) {
  customElements.define(
    'product-carousel',
    class ProductCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        if (!this.track || !this.prevButton || !this.nextButton) return;

        this.onScroll = () => this.update();
        this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
        this.nextButton.addEventListener('click', () => this.scrollByPage(1));
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.update();

        this.setupAutoplay();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopAutoplay();
      }

      get reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // Moves by as many whole cards as fit in the visible area
      scrollByPage(direction) {
        const slide = this.track.querySelector('[data-carousel-slide]');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = slide ? slide.offsetWidth + gap : this.track.clientWidth;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));

        this.track.scrollBy({ left: direction * step * perPage, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      update() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.prevButton.disabled = this.track.scrollLeft <= 1;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
        this.classList.toggle('product-carousel--static', maxScroll <= 1);
      }

      setupAutoplay() {
        this.autoplayDelay = parseInt(this.dataset.autoplay, 10);
        if (!this.autoplayDelay || this.reduceMotion || window.Shopify?.designMode) return;

        this.pauseReasons = new Set(['offscreen']);

        const pause = (reason) => {
          this.pauseReasons.add(reason);
          this.stopAutoplay();
        };
        const resume = (reason) => {
          this.pauseReasons.delete(reason);
          if (this.pauseReasons.size === 0) this.startAutoplay();
        };

        this.addEventListener('mouseenter', () => pause('hover'));
        this.addEventListener('mouseleave', () => resume('hover'));
        this.addEventListener('focusin', () => pause('focus'));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) resume('focus');
        });
        // A swipe or tap means the shopper is browsing on their own: stop for good
        this.track.addEventListener('pointerdown', () => pause('interaction'), { passive: true });

        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) resume('offscreen');
          else pause('offscreen');
        });
        this.visibilityObserver.observe(this);

        this.onVisibilityChange = () => (document.hidden ? pause('hidden') : resume('hidden'));
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      startAutoplay() {
        this.stopAutoplay();
        this.autoplayTimer = setInterval(() => {
          const maxScroll = this.track.scrollWidth - this.track.clientWidth;
          if (maxScroll <= 1) return;

          if (this.track.scrollLeft >= maxScroll - 1) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this.scrollByPage(1);
          }
        }, this.autoplayDelay);
      }

      stopAutoplay() {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}
